import { z } from "zod";
import { ANSWERED_OUTCOMES, CALL_SOURCES } from "./calls.js";
import { checkPeriod } from "./conversations.js";
import { isoDaySchema } from "./filters.js";
import { freeTextSchema } from "./leads.js";
import { actorViewSchema, personSchema } from "./people.js";
import { REPORT_PERIODS, REPORT_WHO_MAX, REPORT_WHO_SEPARATOR, reportWhoIdsOf } from "./reports.js";
import { ID_PATTERN } from "./text.js";
// «Attività» («Chi ha fatto cosa», SEZ-8; M7 parte 2): `GET /api/activity`, il registro di tutte le azioni dell'azienda, anche
// automatiche, una riga per azione, dalla più recente, a pagine con un cursore. Nessuna copia: le righe sono quelle della Timeline
// del Lead, lette da tre tabelle, gli eventi del registro (`activity_events`), le telefonate (`calls`) e le note (`lead_notes`).
// Chi vede che cosa lo decide il server (SEZ-1, RUOLI-14, DATI-3; `apps/server/src/activity/access.ts`).
/**
 * I gruppi di azioni del filtro «Che cosa», in parole comuni: chiamate, esiti, note, WhatsApp, status, owner, Task e richiami, disco e
 * demo, opportunità, sequenza di chiamate e Power Dialer, gli altri dati del Lead, importazioni, impostazioni (con le Smart List) e gli
 * accessi del gestore di Gioia365 (DATI-3).
 */
export const ACTIVITY_GROUPS = [
    "calls",
    "outcomes",
    "notes",
    "whatsapp",
    "status",
    "owner",
    "tasks",
    "appointments",
    "opportunities",
    "sequence",
    "lead_data",
    "imports",
    "settings",
    "platform",
];
/**
 * Il gruppo di ogni tipo di evento del registro. L'elenco dei tipi vive nello schema del database (`ACTIVITY_EVENT_TYPES`): un test
 * del server controlla che ogni tipo abbia qui il suo gruppo, e che qui non ci siano tipi che il registro non conosce.
 */
export const ACTIVITY_EVENT_GROUPS = {
    "appointment.booked": "appointments",
    "appointment.rescheduled": "appointments",
    "appointment.canceled": "appointments",
    "appointment.held": "appointments",
    "appointment.no_show": "appointments",
    "close.meeting": "appointments",
    "appointment.imported": "appointments",
    "opportunity.opened": "opportunities",
    "opportunity.stage_changed": "opportunities",
    "opportunity.sold": "opportunities",
    "opportunity.lost": "opportunities",
    "opportunity.imported": "opportunities",
    "close.opportunity_status_changed": "opportunities",
    "lead.created": "lead_data",
    "lead.field_changed": "lead_data",
    "lead.tag_added": "lead_data",
    "lead.tag_removed": "lead_data",
    "tag.created": "lead_data",
    "lead.phone_changed": "lead_data",
    "lead.second_holder_removed": "lead_data",
    "lead.archived": "lead_data",
    "lead.restored": "lead_data",
    "lead.deleted": "lead_data",
    "lead.merged": "lead_data",
    "lead.merged_away": "lead_data",
    "lead.reentered": "lead_data",
    "close.custom_activity": "lead_data",
    "close.email": "lead_data",
    "ghl.message": "lead_data",
    "ghl.automated_messages": "lead_data",
    "close.lead_merge": "lead_data",
    "close.import_update": "imports",
    "lead.status_changed": "status",
    "lead.owner_changed": "owner",
    "lead.imported": "imports",
    "import.completed": "imports",
    "lead.whatsapp_opted_out": "whatsapp",
    "lead.whatsapp_opted_in": "whatsapp",
    "whatsapp.prepared": "whatsapp",
    "whatsapp.declared_sent": "whatsapp",
    "whatsapp.sent": "whatsapp",
    "whatsapp.failed": "whatsapp",
    "whatsapp.received": "whatsapp",
    "call.outcome_late": "outcomes",
    "lead.phone_marked_wrong": "outcomes",
    "dialer.session_started": "sequence",
    "dialer.session_interrupted": "sequence",
    "dialer.session_finished": "sequence",
    "dialer.lead_skipped": "sequence",
    "dialer.session_paused": "sequence",
    "dialer.session_resumed": "sequence",
    "sequence.started": "sequence",
    "sequence.restarted": "sequence",
    "sequence.attempt_closed": "sequence",
    "sequence.violated": "sequence",
    "sequence.ended": "sequence",
    "sequence.answered_manually": "sequence",
    "sequence.stopped": "sequence",
    "task.created": "tasks",
    "task.rescheduled": "tasks",
    "task.completed": "tasks",
    "task.canceled": "tasks",
    "close.task_completed": "tasks",
    "caller_number.added": "settings",
    "caller_number.verified": "settings",
    "caller_number.verification_failed": "settings",
    "caller_number.assigned": "settings",
    "caller_number.unassigned": "settings",
    "caller_number.removed": "settings",
    "whatsapp_sender.set": "settings",
    "whatsapp_template.updated": "settings",
    "whatsapp_settings.updated": "settings",
    "settings.lead_fields_published": "settings",
    "settings.lead_visibility_changed": "settings",
    "settings.table_layout_published": "settings",
    "settings.call_sequence_changed": "settings",
    "settings.whatsapp_channels_changed": "settings",
    "settings.assignment_changed": "settings",
    "settings.report_goals_changed": "settings",
    "settings.work_blocks_changed": "settings",
    "settings.lead_intake_changed": "settings",
    "intake.address_copied": "settings",
    "intake.address_rotated": "settings",
    "smart_list.created": "settings",
    "smart_list.saved": "settings",
    "smart_list.shared": "settings",
    "smart_list.renamed": "settings",
    "smart_list.archived": "settings",
    "platform.organization_access": "platform",
};
/** I tipi di evento di un gruppo, nell'ordine della tabella: vuoto per «Chiamate» e «Note», che non sono eventi del registro. */
export function activityEventTypesOf(group) {
    return Object.entries(ACTIVITY_EVENT_GROUPS)
        .filter(([, value]) => value === group)
        .map(([type]) => type);
}
/** La lunghezza massima del cursore: il server non ne emette mai uno più lungo. */
export const ACTIVITY_CURSOR_MAX_LENGTH = 200;
// Al più 50 identificativi da 128 caratteri con le virgole fra loro, come «Di chi» del Report.
const WHO_MAX_LENGTH = REPORT_WHO_MAX * 129;
/**
 * Query di `GET /api/activity`. Un parametro sconosciuto, un valore fuori dai limiti o un cursore che non viene da una pagina è `422
 * validation_failed` con il nome del parametro. Il periodo è quello di SEZ-3 («Oggi», «Ieri», «Settimana», «Mese», «Personalizzato»),
 * di partenza «Oggi», con `from` e `to` (`AAAA-MM-GG`) soltanto con `custom` (`checkPeriod`). `who` («Di chi») sono da 1 a 50
 * appartenenze separate da una virgola, come nel Report: vale soltanto per chi vede le azioni del team (`canFilterByPerson`). `group`
 * è il filtro «Che cosa»; `q` cerca il Lead (nomi, salone, email, luogo, cifre dei telefoni), come l'elenco.
 */
export const activityQuerySchema = z
    .strictObject({
    period: z.enum(REPORT_PERIODS).default("today"),
    from: isoDaySchema.optional(),
    to: isoDaySchema.optional(),
    who: z
        .string()
        .max(WHO_MAX_LENGTH)
        .refine((who) => {
        const ids = reportWhoIdsOf(who);
        return ids.length <= REPORT_WHO_MAX && ids.every((id) => ID_PATTERN.test(id));
    }, { message: `who is 1 to ${REPORT_WHO_MAX} membership ids separated by ${REPORT_WHO_SEPARATOR}` })
        .optional(),
    group: z.enum(ACTIVITY_GROUPS).optional(),
    q: freeTextSchema(200).optional(),
    cursor: z.string().max(ACTIVITY_CURSOR_MAX_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
})
    .superRefine(checkPeriod);
/**
 * Il Lead di una riga: nome composto e salone come nell'elenco, e com'è adesso: attivo, archiviato (non ha più una scheda né una
 * Timeline da aprire) o unito in un altro Lead (`mergedInto`, nominato soltanto se chi guarda lo vede: la sua Timeline ha ora le
 * azioni del Lead assorbito).
 */
export const activityLeadSchema = z.object({
    id: z.string(),
    displayName: z.string().nullable(),
    salonName: z.string().nullable(),
    state: z.enum(["active", "archived", "merged"]),
    mergedInto: z.object({ id: z.string(), displayName: z.string().nullable() }).nullable(),
});
/**
 * Le parti comuni di una riga: la chiave (`<tipo>:<id>`), quando, chi (l'istantanea dell'attore: una persona, il gestore di Gioia365,
 * un automatismo come «Sequenza di chiamate» o «Meta», o un utente di Close), la persona osservata se l'azione è stata fatta nella
 * «Guarda come…» (`viewOf`), il gruppo, il Lead e `leadHidden`: l'azione riguarda un Lead fuori dal perimetro di chi guarda
 * (RUOLI-14), che la riga non nomina e non apre (`lead` nullo e nessun dettaglio).
 */
const base = {
    key: z.string(),
    occurredAt: z.string(),
    actor: actorViewSchema,
    viewOf: z.string().nullable(),
    group: z.enum(ACTIVITY_GROUPS),
    lead: activityLeadSchema.nullable(),
    leadHidden: z.boolean(),
};
/**
 * Il dettaglio di un evento del registro, soltanto per un Lead che chi guarda vede o per un evento senza Lead: il campo cambiato e,
 * per status, owner, tag e lo spostamento di un richiamo, il prima e il dopo (gli altri valori dei campi restano nella Timeline);
 * il motivo e il `payload` dell'evento (per un evento senza Lead le sole voci che la riga mostra).
 */
export const activityEventDetailsSchema = z.object({
    fieldLabel: z.string().nullable(),
    before: z.unknown(),
    after: z.unknown(),
    reason: z.string().nullable(),
    payload: z.record(z.string(), z.unknown()),
});
export const activityRowSchema = z.discriminatedUnion("kind", [
    // Un evento del registro: il tipo, la correlazione (il salvataggio di cui fa parte) e il dettaglio (`null` per un Lead nascosto).
    z.object({ kind: z.literal("event"), ...base, eventId: z.string(), type: z.string(), correlationId: z.string(), fromClose: z.boolean(), details: activityEventDetailsSchema.nullable() }),
    // Una telefonata, con l'esito in due passi: la stessa della Timeline e di «Conversazioni»; `live` finché è in corso.
    z.object({
        kind: z.literal("call"),
        ...base,
        callId: z.string(),
        direction: z.enum(["outbound", "inbound"]),
        outcome: z.string().nullable(),
        answeredOutcome: z.enum(ANSWERED_OUTCOMES).nullable(),
        durationSeconds: z.number().int().nullable(),
        valid: z.boolean(),
        live: z.boolean(),
        source: z.enum(CALL_SOURCES),
    }),
    // Una nota o l'appunto di una telefonata (`callId`): senza il testo, che resta nella Timeline del Lead.
    z.object({ kind: z.literal("note"), ...base, noteId: z.string(), callId: z.string().nullable(), fromClose: z.boolean() }),
]);
/** Una pagina; `nextCursor` nullo quando non ce ne sono altre; `canFilterByPerson`: si sceglie «Di chi» (`who`). */
export const activityPageSchema = z.object({ rows: z.array(activityRowSchema), nextCursor: z.string().nullable(), canFilterByPerson: z.boolean() });
/**
 * Le persone di «Di chi» (`GET /api/activity/people`): tutte quelle dell'azienda, anche sospese o archiviate, perché le loro azioni
 * restano, con `active` per dirlo accanto al nome; vuota per chi vede soltanto le sue azioni.
 */
export const activityPeopleResponseSchema = z.object({ people: z.array(personSchema) });
//# sourceMappingURL=activity.js.map